import { useCallback, useEffect, useRef } from "react";

export const COLLAPSED_SIDEBAR_DROPDOWN_OPEN_DELAY = 300;

type UseHoverWithDelayOptions = {
  onOpen: () => void;
  onClose: () => void;
  shouldDisableOpen?: boolean;
  openDelay?: number;
  closeDelay?: number;
};

const useMainNavigationHoverWithDelay = ({
  onOpen,
  onClose,
  shouldDisableOpen = false,
  openDelay = 150,
  closeDelay = 200,
}: UseHoverWithDelayOptions) => {
  const hoverTimeoutRef = useRef<NodeJS.Timeout | undefined>(undefined);

  // Checked when the timer fires, not when it's armed — the condition can change
  // mid-hover (e.g. the sidebar preview opens under a stationary pointer).
  const shouldDisableOpenRef = useRef(shouldDisableOpen);

  useEffect(() => {
    shouldDisableOpenRef.current = shouldDisableOpen;
  }, [shouldDisableOpen]);

  const cancelPending = useCallback(() => {
    if (hoverTimeoutRef.current) {
      clearTimeout(hoverTimeoutRef.current);
      hoverTimeoutRef.current = undefined;
    }
  }, []);

  const handleMouseEnter = useCallback(() => {
    cancelPending();

    hoverTimeoutRef.current = setTimeout(() => {
      if (shouldDisableOpenRef.current) {
        return;
      }
      onOpen();
    }, openDelay);
  }, [onOpen, openDelay, cancelPending]);

  const handleMouseLeave = useCallback(() => {
    cancelPending();

    hoverTimeoutRef.current = setTimeout(() => {
      onClose();
    }, closeDelay);
  }, [onClose, closeDelay, cancelPending]);

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      cancelPending();
    };
  }, [cancelPending]);

  return {
    handleMouseEnter,
    handleMouseLeave,
    cancelPending,
  };
};

export default useMainNavigationHoverWithDelay;
